import { useSyncExternalStore } from 'react';

/**
 * UI-only signal for re-opening the consent modal from the "Manage cookies" control.
 *
 * Kept deliberately separate from the consent store: opening or closing the prompt is a presentational concern and must never notify the service handlers in the registry (they only care about the persisted accept/reject decision). The actual decision still flows through `decide()` in the store.
 */
let isOpen = false;
const listeners = new Set<() => void>();

const emit = (): void => {
  listeners.forEach((listener) => listener());
};

/** Re-fire the consent modal (e.g. from the cookie policy page). */
export const openConsentPrompt = (): void => {
  if (isOpen) {
    return;
  }
  isOpen = true;
  emit();
};

/** Hide the re-fired consent modal (after a decision). */
export const closeConsentPrompt = (): void => {
  if (!isOpen) {
    return;
  }
  isOpen = false;
  emit();
};

const subscribeConsentPrompt = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): boolean => isOpen;

// The prompt is always closed on the server / first paint.
const getServerSnapshot = (): boolean => false;

/** Whether the "Manage cookies" control has re-opened the consent modal. */
export const useConsentPromptOpen = (): boolean =>
  useSyncExternalStore(subscribeConsentPrompt, getSnapshot, getServerSnapshot);
